import React, { useState, useMemo } from 'react';
import {
  Brain, Calendar, Clock, CheckCircle, AlertCircle, TrendingUp, Star,
  GraduationCap, PenTool, BarChart3, Target, Zap, ArrowRight, Globe,
  ChevronDown, ChevronRight, Play, Users, Bell, Settings, Menu,
  MapPin, Trophy, Sparkles, Eye, EyeOff, GripVertical, X,
  FileText, Search, Plus, Edit3, Trash2, MoreHorizontal
} from 'lucide-react';

/**
 * CollegeJourneyHub - Enhanced with Contextual Intelligence
 *
 * Solution #1: Comprehensive Contextual Intelligence System
 *
 * Key Features:
 * 1. Contextual Intelligence Engine that adapts to user situation
 * 2. Strategic Assessment always prominent and accessible
 * 3. College cards preserve current visual excellence with contextual overlays
 * 4. Global view toggle for confidence and control
 * 5. Rich visual design maintained with enhanced depth
 */

// ============================================================================
// CONTEXTUAL INTELLIGENCE ENGINE
// ============================================================================

const useContextualIntelligence = (colleges, timeline, essays, assessmentData) => {
  return useMemo(() => {
    const today = new Date();
    const oneWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    const twoWeeks = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);

    // Analyze timeline for urgent items
    const urgentItems = timeline.filter(item => {
      if (item.completed) return false;
      const dueDate = new Date(item.due_date);
      return dueDate <= oneWeek;
    });

    // Determine current phase
    const totalColleges = colleges.length;
    const essayCount = essays.length;
    const completedEssays = essays.filter(e => e.status === 'completed').length;

    let currentPhase = 'discovery';
    if (totalColleges >= 8 && completedEssays >= 3) currentPhase = 'submission';
    else if (totalColleges >= 6 && essayCount > 0) currentPhase = 'application';
    else if (totalColleges >= 4) currentPhase = 'development';
    else if (assessmentData) currentPhase = 'research';

    // Calculate capacity based on upcoming workload
    const thisWeekTasks = timeline.filter(item => {
      if (item.completed) return false;
      const dueDate = new Date(item.due_date);
      return dueDate <= oneWeek;
    });

    let capacity = 'balanced';
    if (thisWeekTasks.length < 3) capacity = 'light';
    else if (thisWeekTasks.length > 8) capacity = 'heavy';

    // Determine primary context
    let primaryContext = 'progress';
    let contextPriority = 0;

    if (urgentItems.length > 2) {
      primaryContext = 'deadline';
      contextPriority = 3;
    } else if (capacity === 'heavy') {
      primaryContext = 'capacity';
      contextPriority = 2;
    } else if (!assessmentData) {
      primaryContext = 'assessment';
      contextPriority = 4;
    } else if (totalColleges < 4) {
      primaryContext = 'college-building';
      contextPriority = 1;
    }

    // Generate smart recommendations
    const recommendations = [];

    if (!assessmentData) {
      recommendations.push({
        type: 'critical',
        title: 'Complete Strategic Assessment',
        description: 'Unlock personalized AI guidance and strategic insights',
        action: 'Start Assessment',
        icon: Brain,
        color: 'indigo'
      });
    }

    if (urgentItems.length > 0) {
      recommendations.push({
        type: 'urgent',
        title: `${urgentItems.length} Urgent Deadline${urgentItems.length > 1 ? 's' : ''}`,
        description: 'Critical tasks need immediate attention',
        action: 'View Deadlines',
        icon: Bell,
        color: 'coral'
      });
    }

    if (totalColleges < 8 && assessmentData) {
      recommendations.push({
        type: 'important',
        title: 'Build Your College List',
        description: `Add ${8 - totalColleges} more schools for a balanced portfolio`,
        action: 'Find Colleges',
        icon: GraduationCap,
        color: 'teal'
      });
    }

    return {
      primaryContext,
      contextPriority,
      currentPhase,
      capacity,
      urgentItems,
      thisWeekTasks,
      recommendations,
      insights: {
        needsAssessment: !assessmentData,
        portfolioIncomplete: totalColleges < 8,
        essaysNeeded: completedEssays < 3,
        highPressure: thisWeekTasks.length > 8
      }
    };
  }, [colleges, timeline, essays, assessmentData]);
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CollegeJourneyHubEnhanced = () => {
  const [viewMode, setViewMode] = useState('contextual'); // 'contextual' | 'global'
  const [showAssessmentPanel, setShowAssessmentPanel] = useState(false);
  const [currentScenario, setCurrentScenario] = useState('deadline'); // Scenario selector

  // Mock data for demonstration - changes based on scenario
  const getScenarioData = () => {
    switch (currentScenario) {
      case 'deadline':
        return {
          assessmentData: {
            complete: true,
            strengths: ['Leadership', 'STEM Excellence', 'Community Service'],
            profile: 'High-Achieving STEM Leader',
            lastUpdated: '2024-01-15'
          },
          urgentDeadlines: 3,
          overdueItems: 2,
          thisWeekTasks: 8,
          collegeCount: 6,
          essayCount: 3,
          completedEssays: 1
        };

      case 'discovery':
        return {
          assessmentData: null, // No assessment yet
          urgentDeadlines: 0,
          overdueItems: 0,
          thisWeekTasks: 2,
          collegeCount: 0,
          essayCount: 0,
          completedEssays: 0
        };

      case 'heavy-week':
        return {
          assessmentData: {
            complete: true,
            strengths: ['Writing', 'Research', 'Critical Thinking'],
            profile: 'Liberal Arts Scholar',
            lastUpdated: '2024-01-10'
          },
          urgentDeadlines: 1,
          overdueItems: 0,
          thisWeekTasks: 15, // Heavy workload
          collegeCount: 8,
          essayCount: 5,
          completedEssays: 2
        };

      case 'on-track':
        return {
          assessmentData: {
            complete: true,
            strengths: ['Athletics', 'Leadership', 'Academics'],
            profile: 'Scholar-Athlete',
            lastUpdated: '2024-01-05'
          },
          urgentDeadlines: 0,
          overdueItems: 0,
          thisWeekTasks: 4,
          collegeCount: 10,
          essayCount: 8,
          completedEssays: 6
        };

      default:
        return getScenarioData.deadline;
    }
  };

  const scenarioData = getScenarioData();
  const assessmentData = scenarioData.assessmentData;

  const colleges = [
    { id: 1, name: 'Stanford University', category: 'reach', location: 'Stanford, CA',
      applicationStatus: 'in_progress', progress: 65, deadline: '2024-11-01',
      nextAction: 'Complete supplemental essays',
      nextTaskDue: 'Due in 3 days', nextTaskUrgency: 'critical',
      nextTaskDetails: '2 essays remaining: "Why Stanford?" and "What matters to you?"',
      nextTaskEstimate: '4-6 hours' },
    { id: 2, name: 'MIT', category: 'reach', location: 'Cambridge, MA',
      applicationStatus: 'planning', progress: 30, deadline: '2024-11-01',
      nextAction: 'Start application',
      nextTaskDue: 'Due in 5 days', nextTaskUrgency: 'critical',
      nextTaskDetails: 'Begin Common App section and review essay prompts',
      nextTaskEstimate: '2-3 hours' },
    { id: 3, name: 'UC Berkeley', category: 'target', location: 'Berkeley, CA',
      applicationStatus: 'in_progress', progress: 75, deadline: '2024-11-30',
      nextAction: 'Submit transcripts',
      nextTaskDue: 'Due Nov 15', nextTaskUrgency: 'high',
      nextTaskDetails: 'Request official transcripts from high school registrar',
      nextTaskEstimate: '30 minutes' },
    { id: 4, name: 'University of Michigan', category: 'target', location: 'Ann Arbor, MI',
      applicationStatus: 'planning', progress: 20, deadline: '2024-02-01',
      nextAction: 'Research programs',
      nextTaskDue: 'This week', nextTaskUrgency: 'medium',
      nextTaskDetails: 'Research engineering programs and course requirements',
      nextTaskEstimate: '1-2 hours' },
    { id: 5, name: 'Cal Poly SLO', category: 'safety', location: 'San Luis Obispo, CA',
      applicationStatus: 'planning', progress: 10, deadline: '2024-11-30',
      nextAction: 'Begin application',
      nextTaskDue: 'Due Nov 20', nextTaskUrgency: 'medium',
      nextTaskDetails: 'Start Cal State Apply application and gather documents',
      nextTaskEstimate: '1 hour' },
    { id: 6, name: 'San Jose State', category: 'safety', location: 'San Jose, CA',
      applicationStatus: 'not_started', progress: 0, deadline: '2024-11-30',
      nextAction: 'Add to Common App',
      nextTaskDue: 'Due Nov 25', nextTaskUrgency: 'medium',
      nextTaskDetails: 'Add school to application list and review requirements',
      nextTaskEstimate: '15 minutes' }
  ];

  // Dynamic timeline based on scenario
  const getTimelineData = () => {
    const today = new Date();
    const addDays = (days) => new Date(today.getTime() + days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    switch (currentScenario) {
      case 'deadline':
        return [
          { id: 1, title: 'Stanford Supplemental Essays', due_date: addDays(3),
            priority: 'critical', completed: false, college_id: 1 },
          { id: 2, title: 'Request Letter of Rec - Math Teacher', due_date: addDays(5),
            priority: 'high', completed: false },
          { id: 3, title: 'MIT Application Submit', due_date: addDays(7),
            priority: 'critical', completed: false, college_id: 2 },
          { id: 4, title: 'Common App Essay Final', due_date: addDays(-2),
            priority: 'critical', completed: false } // Overdue
        ];

      case 'discovery':
        return [
          { id: 1, title: 'Complete Strategic Assessment', due_date: addDays(14),
            priority: 'high', completed: false },
          { id: 2, title: 'Research College Options', due_date: addDays(21),
            priority: 'medium', completed: false }
        ];

      case 'heavy-week':
        // Many tasks this week
        return Array.from({ length: 15 }, (_, i) => ({
          id: i + 1,
          title: `Task ${i + 1}: ${['Essay Draft', 'Research', 'Application Form', 'Test Prep'][i % 4]}`,
          due_date: addDays(Math.floor(i / 3)),
          priority: i < 5 ? 'high' : 'medium',
          completed: false
        }));

      case 'on-track':
        return [
          { id: 1, title: 'Final Review - Brown Application', due_date: addDays(14),
            priority: 'medium', completed: false },
          { id: 2, title: 'Submit Yale Video', due_date: addDays(21),
            priority: 'low', completed: false },
          { id: 3, title: 'Order Final Transcripts', due_date: addDays(30),
            priority: 'low', completed: false }
        ];

      default:
        return [];
    }
  };

  const timeline = getTimelineData();

  const essays = [
    { id: 1, title: 'Personal Statement', status: 'completed', wordCount: 650 },
    { id: 2, title: 'Stanford - What Matters Most', status: 'in_progress', wordCount: 400 },
    { id: 3, title: 'MIT - Challenge Essay', status: 'not_started', wordCount: 0 }
  ];

  // Get contextual intelligence
  const context = useContextualIntelligence(colleges, timeline, essays, assessmentData);

  // Calculate confidence score
  const confidenceScore = useMemo(() => {
    let score = 0;
    if (assessmentData) score += 40;
    if (colleges.length >= 8) score += 25;
    if (essays.filter(e => e.status === 'completed').length >= 3) score += 20;
    if (context.urgentItems.length === 0) score += 15;
    return Math.min(100, score);
  }, [assessmentData, colleges, essays, context]);

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Strategic Assessment moved to sidebar - no floating button needed */}

      {/* ============================================================================
          HEADER WITH GLOBAL VIEW TOGGLE & CONFIDENCE METER
          ============================================================================ */}
      <header className="bg-slate-900 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <div>
                <h1 className="text-3xl font-bold">College Journey</h1>
                <p className="text-slate-300 text-sm mt-1">Your path to college success</p>
              </div>

              {/* View Mode Toggle in Header */}
              <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('contextual')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'contextual'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Focused View
                  </span>
                </button>
                <button
                  onClick={() => setViewMode('global')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'global'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Overview
                  </span>
                </button>
              </div>
            </div>

            {/* Confidence Meter */}
            <div className="text-right">
              <p className="text-xs text-slate-400 font-medium mb-1">Application Confidence</p>
              <div className="flex items-center gap-3">
                <div className="w-40 h-3 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      confidenceScore >= 80 ? 'bg-gradient-to-r from-emerald-400 to-teal-400' :
                      confidenceScore >= 60 ? 'bg-gradient-to-r from-amber-400 to-yellow-400' :
                      'bg-gradient-to-r from-coral-400 to-red-400'
                    }`}
                    style={{ width: `${confidenceScore}%` }}
                  />
                </div>
                <span className={`text-2xl font-bold ${
                  confidenceScore >= 80 ? 'text-emerald-400' :
                  confidenceScore >= 60 ? 'text-amber-400' :
                  'text-coral-400'
                }`}>
                  {confidenceScore}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ============================================================================
          SUB-HEADER WITH SCENARIO SELECTOR & VIEW TOGGLE
          ============================================================================ */}
      <div className="bg-white border-b border-slate-200 sticky top-[88px] z-30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Scenario Selector Bar */}
          {viewMode === 'contextual' && (
            <div className="mb-3 pb-3 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-slate-600">Scenario Demo:</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentScenario('deadline')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        currentScenario === 'deadline'
                          ? 'bg-coral-100 text-coral-700 border-2 border-coral-400'
                          : 'bg-white border border-slate-300 text-slate-600 hover:border-slate-400'
                      }`}
                    >
                      🔥 Urgent Deadlines
                    </button>
                    <button
                      onClick={() => setCurrentScenario('discovery')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        currentScenario === 'discovery'
                          ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-400'
                          : 'bg-white border border-slate-300 text-slate-600 hover:border-slate-400'
                      }`}
                    >
                      🚀 Just Starting
                    </button>
                    <button
                      onClick={() => setCurrentScenario('heavy-week')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        currentScenario === 'heavy-week'
                          ? 'bg-amber-100 text-amber-700 border-2 border-amber-400'
                          : 'bg-white border border-slate-300 text-slate-600 hover:border-slate-400'
                      }`}
                    >
                      😰 Heavy Week
                    </button>
                    <button
                      onClick={() => setCurrentScenario('on-track')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        currentScenario === 'on-track'
                          ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-400'
                          : 'bg-white border border-slate-300 text-slate-600 hover:border-slate-400'
                      }`}
                    >
                      ✅ On Track
                    </button>
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  Click scenarios to see how the interface adapts
                </div>
              </div>
            </div>
          )}

          {/* View Mode Toggle - Centered */}
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
              <button
                onClick={() => setViewMode('contextual')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'contextual'
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Focused View
                </span>
              </button>
              <button
                onClick={() => setViewMode('global')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'global'
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Overview
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================================
          MAIN CONTENT AREA
          ============================================================================ */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {viewMode === 'contextual' ? (
          <ContextualView context={context} colleges={colleges} essays={essays} />
        ) : (
          <GlobalView colleges={colleges} timeline={timeline} essays={essays} />
        )}
      </main>
    </div>
  );
};

// ============================================================================
// PRIMARY CONTEXT CARD - Adapts to Most Important Focus
// ============================================================================

const PrimaryContextCard = ({ context }) => {
  // For "Just Starting" phase - show distinctive layout like your example
  if (context.currentPhase === 'discovery') {
    return (
      <div className="space-y-6">
        {/* Strategic Foundation Card - Like your example */}
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl border-2 border-teal-400 p-8">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-teal-100 rounded-xl">
              <Target className="w-6 h-6 text-teal-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Strategic Foundation</h2>
              <p className="text-slate-600 mb-6">Complete your assessment to unlock personalized guidance</p>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900">Complete Strategic Assessment</h3>
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                  <p className="text-xs text-slate-600 mb-3">Progress</p>
                  <div className="h-2 bg-slate-200 rounded-full mb-3">
                    <div className="h-2 bg-red-500 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <button className="w-full py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700">
                    Get Started
                  </button>
                </div>

                <div className="bg-white rounded-xl p-4 opacity-60">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900">Build Academic Profile</h3>
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  </div>
                  <p className="text-xs text-slate-600 mb-3">Progress</p>
                  <div className="h-2 bg-slate-200 rounded-full mb-3">
                    <div className="h-2 bg-teal-500 rounded-full" style={{ width: '30%' }}></div>
                  </div>
                  <button className="w-full py-2 bg-slate-100 text-slate-400 rounded-lg font-medium" disabled>
                    Continue
                  </button>
                </div>

                <div className="bg-white rounded-xl p-4 opacity-60">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900">Define College Preferences</h3>
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  </div>
                  <p className="text-xs text-slate-600 mb-3">Progress</p>
                  <div className="h-2 bg-slate-200 rounded-full mb-3">
                    <div className="h-2 bg-slate-300 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                  <button className="w-full py-2 bg-slate-100 text-slate-400 rounded-lg font-medium" disabled>
                    Get Started
                  </button>
                </div>

                <div className="bg-white rounded-xl p-4 opacity-60">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900">Research Career Interests</h3>
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  </div>
                  <p className="text-xs text-slate-600 mb-3">Progress</p>
                  <div className="h-2 bg-slate-200 rounded-full mb-3">
                    <div className="h-2 bg-slate-300 rounded-full" style={{ width: '10%' }}></div>
                  </div>
                  <button className="w-full py-2 bg-slate-100 text-slate-400 rounded-lg font-medium" disabled>
                    Continue
                  </button>
                </div>
              </div>

              <button className="mt-6 px-6 py-3 bg-teal-600 text-white rounded-xl font-semibold shadow-lg hover:bg-teal-700 transition-all">
                Start Assessment
                <ArrowRight className="inline-block w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Special layout for Urgent Deadlines context
  if (context.primaryContext === 'deadline') {
    const getActionText = (itemType) => {
      switch (itemType) {
        case 'essay': return 'Write Essay';
        case 'application': return 'Continue App';
        case 'recommendation': return 'Follow Up';
        case 'transcript': return 'Submit Docs';
        default: return 'Take Action';
      }
    };

    const getActionColor = (daysLeft) => {
      if (daysLeft <= 3) return 'bg-red-600 hover:bg-red-700';
      if (daysLeft <= 7) return 'bg-coral-500 hover:bg-coral-600';
      return 'bg-amber-500 hover:bg-amber-600';
    };

    return (
      <div className="space-y-6">
        {/* Urgent Header */}
        <div className="bg-gradient-to-r from-red-500 to-coral-500 text-white rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Bell className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2">🚨 Critical Deadlines</h2>
              <p className="text-red-100 text-lg">
                {context.urgentItems.length} urgent items need immediate action
              </p>
            </div>
          </div>
        </div>

        {/* Direct Action Items */}
        <div className="space-y-4">
          {context.urgentItems.slice(0, 4).map((item, idx) => {
            const daysLeft = Math.ceil((new Date(item.due_date) - new Date()) / (1000 * 60 * 60 * 24));

            return (
              <div key={idx} className="bg-white border-2 border-slate-900 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-white font-bold text-xl ${
                      daysLeft <= 3 ? 'bg-red-500' :
                      daysLeft <= 7 ? 'bg-coral-500' :
                      'bg-amber-500'
                    }`}>
                      {daysLeft > 0 ? daysLeft : 'DUE'}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-slate-600">
                        {item.college_name} • Due {new Date(item.due_date).toLocaleDateString()}
                      </p>
                      {daysLeft <= 0 && (
                        <p className="text-red-600 font-semibold text-sm mt-1">⚠️ OVERDUE</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                      item.type === 'application' ? 'bg-violet-100 text-violet-800' :
                      item.type === 'essay' ? 'bg-azure-100 text-azure-800' :
                      item.type === 'recommendation' ? 'bg-emerald-100 text-emerald-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {item.type}
                    </span>

                    <button
                      onClick={() => {
                        // Direct action - would launch specific interface
                        console.log(`Direct action: ${item.type} for ${item.title}`);
                      }}
                      className={`px-6 py-3 text-white rounded-xl font-bold transition-all hover:scale-105 shadow-lg ${
                        getActionColor(daysLeft)
                      }`}
                    >
                      {getActionText(item.type)}
                    </button>
                  </div>
                </div>

                {/* Progress indicator if available */}
                {item.progress && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-slate-600">Progress</span>
                      <span className="font-semibold text-slate-900">{item.progress}%</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-500"
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Emergency Help */}
        <div className="bg-slate-50 border-2 border-slate-300 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-slate-200 rounded-xl">
              <Zap className="w-6 h-6 text-slate-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-slate-900 mb-1">Need Help Managing Deadlines?</h3>
              <p className="text-slate-600 text-sm">
                Get personalized task prioritization and time management strategies
              </p>
            </div>
            <button className="px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800">
              Get Help
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Regular context content for other phases
  const getContextContent = () => {
    switch (context.primaryContext) {
      case 'deadline':
        // Special handling for deadline context - completely different layout
        return null; // We'll handle this separately

      case 'college-building':
        return {
          title: 'Build Your College Portfolio',
          subtitle: 'Add more schools for a balanced application strategy',
          icon: <GraduationCap className="w-8 h-8" />,
          bgGradient: 'from-teal-500 to-emerald-500',
          bgPattern: 'bg-teal-50',
          borderColor: 'border-teal-500',
          items: [
            { title: 'Add Reach Schools', subtitle: '2-3 ambitious choices' },
            { title: 'Find Target Schools', subtitle: '3-4 strong matches' },
            { title: 'Select Safety Schools', subtitle: '2-3 likely admits' }
          ]
        };

      default:
        return {
          title: 'Your Journey is On Track',
          subtitle: 'Continue making steady progress',
          icon: <CheckCircle className="w-8 h-8" />,
          bgGradient: 'from-emerald-500 to-teal-500',
          bgPattern: 'bg-emerald-50',
          borderColor: 'border-emerald-500',
          items: []
        };
    }
  };

  const content = getContextContent();

  return (
    <div className={`${content.bgPattern} border-2 ${content.borderColor} rounded-2xl p-8 relative overflow-hidden`}>
      {/* Background Decoration */}
      <div className={`absolute top-0 right-0 w-64 h-64 bg-gradient-to-br ${content.bgGradient} opacity-10 rounded-full transform translate-x-32 -translate-y-32`}></div>

      <div className="relative z-10">
        <div className="flex items-start gap-6">
          <div className={`p-3 bg-gradient-to-br ${content.bgGradient} text-white rounded-xl shadow-lg`}>
            {content.icon}
          </div>

          <div className="flex-1">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {content.title}
            </h2>
            <p className="text-slate-600 mb-6">
              {content.subtitle}
            </p>

            {content.items.length > 0 && (
              <div className="space-y-3">
                {content.items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm">
                    <div className={`w-2 h-2 rounded-full bg-gradient-to-br ${content.bgGradient}`}></div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{item.title}</p>
                      <p className="text-sm text-slate-600">{item.subtitle}</p>
                    </div>
                    {item.urgent && (
                      <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                        URGENT
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button className={`mt-6 px-6 py-3 bg-gradient-to-r ${content.bgGradient} text-white rounded-lg font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200`}>
              Take Action
              <ArrowRight className="inline-block w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CONTEXTUAL VIEW - Intelligent Focus Based on User Context
// ============================================================================
const ContextualView = ({ context, colleges, essays }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Main Content - 3 columns */}
      <div className="lg:col-span-3 space-y-8">
        {/* Primary Context Card */}
        <PrimaryContextCard context={context} />

        {/* Smart Recommendations */}
        {context.recommendations.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {context.recommendations.map((rec, idx) => (
              <RecommendationCard key={idx} recommendation={rec} />
            ))}
          </div>
        )}

        {/* College Cards - Contextually Organized */}
        <CollegeCardsContextual colleges={colleges} context={context} />

        {/* Secondary Actions */}
        <SecondaryActions context={context} essays={essays} />
      </div>

      {/* Sidebar - Week at a Glance */}
      <div className="lg:col-span-1">
        <WeekAtGlance context={context} />
      </div>
    </div>
  );
};

// ============================================================================
// WEEK AT A GLANCE COMPONENT
// ============================================================================

const WeekAtGlance = ({ context }) => {
  const today = new Date();
  const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const currentDay = today.getDay() === 0 ? 6 : today.getDay() - 1; // Convert to Mon-Sun

  // Get this week's dates
  const getWeekDates = () => {
    const dates = [];
    const monday = new Date(today);
    monday.setDate(today.getDate() - currentDay);

    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push(date.getDate());
    }
    return dates;
  };

  const weekDates = getWeekDates();
  const tasksCompleted = context.primaryContext === 'on-track' ? 3 : 0;
  const totalTasks = context.thisWeekTasks.length;

  return (
    <div className="sticky top-[200px] space-y-4">
      {/* Week Overview Card */}
      <div className="bg-white rounded-xl border-2 border-slate-900 p-6 shadow-lg">
        <h3 className="font-bold text-lg text-slate-900 mb-4 uppercase tracking-tight">Week at a Glance</h3>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 mb-6">
          {weekDays.map((day, idx) => (
            <div key={idx} className="text-center">
              <div className="text-xs text-slate-500 mb-1">{day}</div>
              <div className={`
                aspect-square flex items-center justify-center rounded-lg text-sm font-medium
                ${idx === currentDay
                  ? 'bg-teal-600 text-white ring-2 ring-teal-600 ring-offset-2'
                  : 'bg-slate-100 text-slate-700'}
              `}>
                {weekDates[idx]}
              </div>
              {idx === currentDay && (
                <div className="text-xs text-teal-600 mt-1 font-medium">11h</div>
              )}
            </div>
          ))}
        </div>

        {/* Progress Circle */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <svg className="w-32 h-32 transform -rotate-90">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="#e2e8f0"
                strokeWidth="12"
                fill="none"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="#14b8a6"
                strokeWidth="12"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 56}`}
                strokeDashoffset={`${2 * Math.PI * 56 * (1 - tasksCompleted / Math.max(totalTasks, 1))}`}
                className="transition-all duration-500"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-3xl font-bold text-slate-900">{tasksCompleted}/{totalTasks}</div>
              <div className="text-xs text-slate-600">tasks done</div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Remaining:</span>
            <span className="text-sm font-semibold text-slate-900">
              {totalTasks - tasksCompleted} tasks • ~{((totalTasks - tasksCompleted) * 1.5).toFixed(1)}h
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Tomorrow:</span>
            <span className="text-sm font-semibold text-slate-900">
              {context.capacity === 'heavy' ? '5 tasks • 7.5h' : '0 tasks • 0h'}
            </span>
          </div>
        </div>
      </div>

      {/* Workload Alert */}
      {context.capacity === 'heavy' && (
        <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Heavy Week</p>
              <p className="text-xs text-amber-700">Consider rescheduling non-critical tasks</p>
            </div>
          </div>
        </div>
      )}

      {/* Strategic Playbook Card */}
      <StrategicPlaybookCard />
    </div>
  );
};

// ============================================================================
// STRATEGIC PLAYBOOK CARD - Sidebar Component
// ============================================================================

const StrategicPlaybookCard = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Mock assessment data
  const assessmentData = {
    complete: true,
    profile: 'High-Achieving STEM Leader',
    strengths: ['Leadership', 'STEM Excellence', 'Community Service'],
    lastUpdated: '2024-01-15',
    needsRetake: false, // Based on changes in profile or new opportunities
    confidence: 92
  };

  if (!assessmentData.complete) {
    return (
      <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border-2 border-indigo-400 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Brain className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900">Strategic Assessment</h3>
            <p className="text-xs text-slate-600">Get personalized guidance</p>
          </div>
        </div>
        <button className="w-full py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors">
          Start Assessment
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-slate-900 rounded-xl p-4 shadow-lg">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-lg">
          <Brain className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-slate-900">Strategic Playbook</h3>
            {assessmentData.needsRetake && (
              <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
            )}
          </div>
          <p className="text-xs text-slate-600">Your personalized strategy</p>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-slate-600" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-600" />
          )}
        </button>
      </div>

      {/* Compact View */}
      {!isExpanded && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-600">Profile:</span>
            <span className="text-xs font-medium text-slate-900">{assessmentData.profile}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-600">Confidence:</span>
            <span className="text-xs font-bold text-emerald-600">{assessmentData.confidence}%</span>
          </div>
          <button className="w-full py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-lg text-xs font-medium transition-colors">
            Quick Review
          </button>
        </div>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div className="space-y-4">
          <div>
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
              Your Profile
            </h4>
            <p className="text-sm font-medium text-slate-900 mb-2">{assessmentData.profile}</p>
            <p className="text-xs text-slate-600">
              Last updated: {new Date(assessmentData.lastUpdated).toLocaleDateString()}
            </p>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
              Key Strengths
            </h4>
            <div className="flex flex-wrap gap-1">
              {assessmentData.strengths.map((strength, idx) => (
                <span key={idx} className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full font-medium">
                  {strength}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
              Strategy Confidence
            </h4>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
                  style={{ width: `${assessmentData.confidence}%` }}
                />
              </div>
              <span className="text-sm font-bold text-emerald-600">{assessmentData.confidence}%</span>
            </div>
          </div>

          {assessmentData.needsRetake && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span className="text-xs font-semibold text-amber-800">Update Suggested</span>
              </div>
              <p className="text-xs text-amber-700 mb-2">
                Your profile may benefit from updated insights based on recent accomplishments.
              </p>
              <button className="text-xs text-amber-800 font-medium hover:text-amber-900">
                Take Quick Update →
              </button>
            </div>
          )}

          <div className="flex gap-2">
            <button className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition-colors">
              Full Analysis
            </button>
            <button className="flex-1 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-lg text-xs font-medium transition-colors">
              Retake
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================

// ============================================================================
// RECOMMENDATION CARDS - Smart AI-Driven Suggestions
// ============================================================================

const RecommendationCard = ({ recommendation }) => {
  const Icon = recommendation.icon;
  const colorMap = {
    indigo: 'from-indigo-500 to-violet-500',
    coral: 'from-coral-500 to-red-500',
    teal: 'from-teal-500 to-emerald-500',
    gold: 'from-amber-500 to-yellow-500'
  };

  return (
    <div className="group bg-white rounded-xl border-2 border-slate-200 hover:border-slate-400 shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden">
      <div className={`h-2 bg-gradient-to-r ${colorMap[recommendation.color]}`}></div>
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className={`p-2 bg-gradient-to-br ${colorMap[recommendation.color]} text-white rounded-lg`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 mb-1">
              {recommendation.title}
            </h3>
            <p className="text-sm text-slate-600">
              {recommendation.description}
            </p>
          </div>
        </div>
        <button className="w-full py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-lg text-sm font-medium transition-colors">
          {recommendation.action}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// COLLEGE CARDS - Preserving Visual Excellence with Contextual Enhancements
// ============================================================================

const CollegeCardsContextual = ({ colleges, context }) => {
  // Sort colleges based on context
  const sortedColleges = useMemo(() => {
    if (context.primaryContext === 'deadline') {
      return [...colleges].sort((a, b) => {
        const dateA = new Date(a.deadline);
        const dateB = new Date(b.deadline);
        return dateA - dateB;
      });
    }
    return colleges;
  }, [colleges, context]);

  const getCategoryColor = (category) => {
    switch (category) {
      case 'reach': return 'from-pink-500 to-rose-500';
      case 'target': return 'from-amber-500 to-orange-500';
      case 'safety': return 'from-emerald-500 to-emerald-500';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const getUrgencyBadge = (deadline) => {
    const daysUntil = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));

    if (daysUntil <= 7) {
      return { text: `${daysUntil}d`, color: 'bg-red-500', pulse: true };
    } else if (daysUntil <= 14) {
      return { text: `${daysUntil}d`, color: 'bg-amber-500', pulse: false };
    } else if (daysUntil <= 30) {
      return { text: `${daysUntil}d`, color: 'bg-yellow-500', pulse: false };
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900">Your Colleges</h2>
        <button className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-900 rounded-lg font-medium hover:bg-slate-50 transition-colors">
          <Plus className="w-4 h-4" />
          Add College
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedColleges.map((college) => {
          const urgencyBadge = getUrgencyBadge(college.deadline);

          return (
            <div
              key={college.id}
              className="group relative bg-white rounded-xl border-2 border-slate-900 shadow-sm hover:shadow-2xl transition-all duration-200 transform hover:translate-y-[-4px] cursor-move overflow-hidden"
            >
              {/* Gradient Header - Preserved from Original Design */}
              <div className={`h-16 bg-gradient-to-r ${getCategoryColor(college.category)} relative`}>
                {/* Drag Handle */}
                <div className="absolute top-2 left-2 text-white/60 group-hover:text-white/90 transition-colors">
                  <GripVertical className="w-5 h-5" />
                </div>

                {/* Urgency Badge Overlay */}
                {urgencyBadge && (
                  <div className="absolute top-2 right-2">
                    <span className={`px-2 py-1 ${urgencyBadge.color} text-white text-xs rounded-full font-bold ${
                      urgencyBadge.pulse ? 'animate-pulse' : ''
                    }`}>
                      {urgencyBadge.text}
                    </span>
                  </div>
                )}
              </div>

              {/* College Info */}
              <div className="p-5">
                <div className="mb-4">
                  <h3 className="font-bold text-lg text-slate-900 mb-1">{college.name}</h3>
                  <p className="text-sm text-slate-600 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {college.location}
                  </p>
                </div>

                {/* Progress Bar - Visual Excellence Preserved */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-600">Progress</span>
                    <span className="text-xs font-bold text-slate-900">{college.progress}%</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${getCategoryColor(college.category)} transition-all duration-500`}
                      style={{ width: `${college.progress}%` }}
                    />
                  </div>
                </div>

                {/* Next Task Info - Enhanced with Details */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-slate-600">Next Task</p>
                    {college.nextTaskDue && (
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        college.nextTaskUrgency === 'critical' ? 'bg-red-100 text-red-700' :
                        college.nextTaskUrgency === 'high' ? 'bg-amber-100 text-amber-700' :
                        'bg-azure-100 text-azure-700'
                      }`}>
                        {college.nextTaskDue}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-900 font-medium mb-1">{college.nextAction}</p>
                  {college.nextTaskDetails && (
                    <p className="text-xs text-slate-600">{college.nextTaskDetails}</p>
                  )}
                  {college.nextTaskEstimate && (
                    <div className="flex items-center gap-1 mt-2">
                      <Clock className="w-3 h-3 text-slate-400" />
                      <span className="text-xs text-slate-500">{college.nextTaskEstimate}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-4">
                  <button className="flex-1 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors">
                    Continue
                  </button>
                  <button className="p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// SECONDARY ACTIONS - Additional Context-Relevant Actions
// ============================================================================

const SecondaryActions = ({ context, essays }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <PenTool className="w-5 h-5 text-indigo-600" />
          </div>
          <h3 className="font-semibold text-slate-900">Essay Progress</h3>
        </div>
        <div className="space-y-2">
          {essays.map((essay) => (
            <div key={essay.id} className="flex items-center justify-between">
              <span className="text-sm text-slate-600">{essay.title}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                essay.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                essay.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                'bg-slate-100 text-slate-600'
              }`}>
                {essay.status.replace('_', ' ')}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-teal-100 rounded-lg">
            <Trophy className="w-5 h-5 text-teal-600" />
          </div>
          <h3 className="font-semibold text-slate-900">This Week's Goal</h3>
        </div>
        <p className="text-2xl font-bold text-slate-900 mb-2">
          Complete {context.thisWeekTasks.length} tasks
        </p>
        <p className="text-sm text-slate-600">
          Stay on track for successful applications
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-violet-100 rounded-lg">
            <Sparkles className="w-5 h-5 text-violet-600" />
          </div>
          <h3 className="font-semibold text-slate-900">AI Insights</h3>
        </div>
        <p className="text-sm text-slate-600 mb-3">
          Based on your profile, consider adding more target schools in California
        </p>
        <button className="text-sm text-violet-600 font-medium hover:text-violet-700">
          View Recommendations →
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// GLOBAL VIEW - Complete Overview for Confidence and Control
// ============================================================================

const GlobalView = ({ colleges, timeline, essays }) => {
  const categories = ['reach', 'target', 'safety'];
  const targets = { reach: 4, target: 4, safety: 4 };
  const categoryColors = {
    reach: 'from-pink-500 to-rose-500',
    target: 'from-amber-500 to-orange-500',
    safety: 'from-emerald-500 to-emerald-500'
  };

  return (
    <div className="space-y-8">
      {/* Complete Portfolio Overview - Visual Card Layout */}
      <div className="bg-white rounded-2xl border-2 border-slate-900 shadow-xl p-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-slate-900">Complete Portfolio Overview</h2>
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg">
            <span className="text-sm text-slate-600">Total Schools:</span>
            <span className="text-lg font-bold text-slate-900">{colleges.length}/12</span>
          </div>
        </div>

        {categories.map((category) => {
          const categoryColleges = colleges.filter(c => c.category === category);
          const count = categoryColleges.length;
          const target = targets[category];

          return (
            <div key={category} className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-1 h-8 bg-gradient-to-b ${categoryColors[category]} rounded-full`}></div>
                  <h3 className="text-lg font-semibold text-slate-900 capitalize">
                    {category === 'reach' ? 'Reach' : category} Schools
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${
                    count >= target ? 'text-emerald-600' : 'text-amber-600'
                  }`}>
                    {count} of {target}
                  </span>
                  <span className="text-xs text-slate-500">schools</span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {categoryColleges.map((college) => {
                  // Find next task for this college
                  const collegeTimeline = timeline.filter(t => t.college_id === college.id && !t.completed);
                  const nextTask = collegeTimeline.sort((a, b) => new Date(a.due_date) - new Date(b.due_date))[0];

                  return (
                    <div key={college.id} className="group relative bg-white rounded-xl border-2 border-slate-200 hover:border-slate-400 shadow-sm hover:shadow-lg transition-all overflow-hidden">
                      <div className={`h-2 bg-gradient-to-r ${categoryColors[category]}`}></div>
                      <div className="p-4">
                        <h4 className="font-bold text-slate-900 text-sm mb-1 uppercase tracking-tight">
                          {college.name}
                        </h4>
                        <p className="text-xs text-slate-600 mb-3">{college.location}</p>

                        <div className="space-y-3">
                          <div className="text-xs">
                            <span className="text-slate-500">PROGRESS</span>
                            <div className="h-2 bg-slate-100 rounded-full mt-1">
                              <div className={`h-2 bg-gradient-to-r ${categoryColors[category]} rounded-full`}
                                style={{ width: `${college.progress}%` }}
                              />
                            </div>
                          </div>

                          {/* Next Task Due */}
                          {nextTask && (
                            <div className="p-2 bg-slate-50 rounded-lg border border-slate-200">
                              <p className="text-xs text-slate-500 mb-1">NEXT DUE:</p>
                              <div className="flex items-center justify-between">
                                <p className="text-xs font-semibold text-slate-900 leading-tight">
                                  {nextTask.title.length > 20 ? `${nextTask.title.substring(0, 20)}...` : nextTask.title}
                                </p>
                                <span className={`text-xs font-bold ${
                                  Math.ceil((new Date(nextTask.due_date) - new Date()) / (1000 * 60 * 60 * 24)) <= 7
                                    ? 'text-coral-600'
                                    : 'text-slate-600'
                                }`}>
                                  {new Date(nextTask.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* No upcoming tasks */}
                          {!nextTask && (
                            <div className="p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                              <p className="text-xs font-medium text-emerald-700">✓ All caught up!</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {/* Add placeholder slots */}
                {[...Array(Math.max(0, target - count))].map((_, idx) => (
                  <button key={`placeholder-${idx}`} className="relative bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-100 transition-all p-8">
                    <Plus className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-xs text-slate-500">Add {category === 'reach' ? 'Reach' : category} School</p>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Complete Essay Overview - Visual Cards */}
      <div className="bg-white rounded-2xl border-2 border-slate-900 shadow-xl p-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-slate-900">Complete Essay Overview</h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
              <span className="text-sm text-slate-600">Complete</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
              <span className="text-sm text-slate-600">In Progress</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-slate-300 rounded-full"></div>
              <span className="text-sm text-slate-600">Not Started</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {essays.map((essay) => (
            <div key={essay.id} className="bg-white rounded-xl border-2 border-slate-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-bold text-slate-900">{essay.title}</h3>
                <div className={`w-3 h-3 rounded-full ${
                  essay.status === 'completed' ? 'bg-emerald-500' :
                  essay.status === 'in_progress' ? 'bg-amber-500' :
                  'bg-slate-300'
                }`}></div>
              </div>
              <p className="text-sm text-slate-600 mb-4 line-clamp-3">
                {essay.status === 'completed' ? 'A compelling narrative about...' :
                 essay.status === 'in_progress' ? 'Currently developing ideas about...' :
                 'Click to start writing...'}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">{essay.wordCount} words</span>
                <button className="text-sm text-teal-600 font-medium hover:text-teal-700">
                  {essay.status === 'completed' ? 'Review' :
                   essay.status === 'in_progress' ? 'Continue' :
                   'Start'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Enhanced Recommendations & References Overview */}
      <div className="bg-white rounded-2xl border-2 border-slate-900 shadow-xl p-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-8">Recommendations & References</h2>

        {/* Student Brag Sheet Status */}
        <div className="mb-8 p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border-2 border-emerald-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <FileText className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Student Brag Sheet</h3>
                <p className="text-sm text-slate-600">Master document with all activities and achievements</p>
              </div>
            </div>
            <span className="px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-sm font-bold">
              ✓ Complete
            </span>
          </div>
        </div>

        {/* Individual Recommenders */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              name: 'Dr. Johnson',
              role: 'Math Teacher',
              subject: 'Calculus BC',
              bragSheetComplete: true,
              letterStatus: 'Submitted',
              schools: 'All schools',
              deadline: null,
              statusColor: 'emerald'
            },
            {
              name: 'Ms. Chen',
              role: 'Science Teacher',
              subject: 'AP Physics',
              bragSheetComplete: true,
              letterStatus: 'In Progress',
              schools: '8 schools',
              deadline: 'Nov 15',
              statusColor: 'amber'
            },
            {
              name: 'Mr. Williams',
              role: 'Counselor',
              subject: 'College Counseling',
              bragSheetComplete: false,
              letterStatus: 'Pending',
              schools: 'All schools',
              deadline: 'Nov 30',
              statusColor: 'slate'
            }
          ].map((recommender, idx) => (
            <div key={idx} className={`rounded-xl p-6 border-2 ${
              recommender.statusColor === 'emerald' ? 'bg-gradient-to-br from-emerald-50 to-emerald-50 border-emerald-200' :
              recommender.statusColor === 'amber' ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200' :
              'bg-gradient-to-br from-slate-50 to-stone-50 border-slate-200'
            }`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    recommender.statusColor === 'emerald' ? 'bg-emerald-100' :
                    recommender.statusColor === 'amber' ? 'bg-amber-100' :
                    'bg-slate-100'
                  }`}>
                    <Users className={`w-5 h-5 ${
                      recommender.statusColor === 'emerald' ? 'text-emerald-600' :
                      recommender.statusColor === 'amber' ? 'text-amber-600' :
                      'text-slate-600'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{recommender.name}</h3>
                    <p className="text-sm text-slate-600">{recommender.role}</p>
                    <p className="text-xs text-slate-500">{recommender.subject}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {/* Brag Sheet Status */}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Brag Sheet</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    recommender.bragSheetComplete
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {recommender.bragSheetComplete ? '✓ Sent' : 'Pending'}
                  </span>
                </div>

                {/* Letter Status */}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Letter</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    recommender.letterStatus === 'Submitted' ? 'bg-emerald-100 text-emerald-700' :
                    recommender.letterStatus === 'In Progress' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {recommender.letterStatus}
                  </span>
                </div>

                {/* Schools */}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Schools</span>
                  <span className="text-xs font-semibold text-slate-900">{recommender.schools}</span>
                </div>

                {/* Deadline if applicable */}
                {recommender.deadline && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Deadline</span>
                    <span className="text-xs font-semibold text-coral-600">{recommender.deadline}</span>
                  </div>
                )}
              </div>

              {/* Action button if needed */}
              {!recommender.bragSheetComplete && (
                <button className="w-full mt-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">
                  Send Brag Sheet
                </button>
              )}
            </div>
          ))}

          {/* Add Recommender */}
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 border-2 border-dashed border-slate-300">
            <button className="w-full h-full flex flex-col items-center justify-center gap-3 hover:bg-slate-100 rounded-lg transition-colors">
              <Plus className="w-8 h-8 text-slate-400" />
              <span className="text-sm text-slate-600">Add Recommender</span>
            </button>
          </div>
        </div>
      </div>

      {/* Profile Completion Section */}
      <div className="bg-white rounded-2xl border-2 border-slate-900 shadow-xl p-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-8">Profile Completion</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              title: 'Academic Profile',
              progress: 95,
              status: 'Nearly Complete',
              missing: ['SAT Subject Tests'],
              color: 'emerald'
            },
            {
              title: 'Activity Profile',
              progress: 80,
              status: 'Good Progress',
              missing: ['Leadership descriptions', 'Award details'],
              color: 'amber'
            },
            {
              title: 'Testing Profile',
              progress: 100,
              status: 'Complete',
              missing: [],
              color: 'emerald'
            }
          ].map((profile, idx) => (
            <div key={idx} className={`rounded-xl p-6 border-2 ${
              profile.color === 'emerald' ? 'bg-gradient-to-br from-emerald-50 to-emerald-50 border-emerald-200' :
              'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-900">{profile.title}</h3>
                <span className="text-2xl font-bold text-slate-900">{profile.progress}%</span>
              </div>

              <div className="mb-4">
                <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      profile.color === 'emerald' ? 'bg-gradient-to-r from-emerald-500 to-emerald-500' :
                      'bg-gradient-to-r from-amber-500 to-yellow-500'
                    }`}
                    style={{ width: `${profile.progress}%` }}
                  />
                </div>
              </div>

              <p className={`text-sm font-medium mb-3 ${
                profile.color === 'emerald' ? 'text-emerald-700' : 'text-amber-700'
              }`}>
                {profile.status}
              </p>

              {profile.missing.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-600 uppercase tracking-wider mb-2">Missing:</p>
                  <ul className="space-y-1">
                    {profile.missing.map((item, i) => (
                      <li key={i} className="text-xs text-slate-700">• {item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Financial Aid & Scholarships Section */}
      <div className="bg-white rounded-2xl border-2 border-slate-900 shadow-xl p-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-8">Financial Aid & Scholarships</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* FAFSA & Aid */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-slate-900">Federal Aid</h3>

            <div className="p-6 bg-gradient-to-br from-azure-50 to-cyan-50 rounded-xl border-2 border-azure-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-azure-100 rounded-lg">
                    <FileText className="w-5 h-5 text-azure-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">FAFSA</h4>
                    <p className="text-sm text-slate-600">Free Application for Federal Student Aid</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-bold">
                  In Progress
                </span>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Opens:</span>
                  <span className="font-medium">October 1, 2024</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Priority Deadline:</span>
                  <span className="font-medium text-coral-600">January 1, 2025</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Progress:</span>
                  <span className="font-medium">65% Complete</span>
                </div>
              </div>

              <button className="w-full mt-4 py-2 bg-azure-600 text-white rounded-lg font-medium hover:bg-azure-700">
                Continue FAFSA
              </button>
            </div>
          </div>

          {/* Scholarships */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Scholarships</h3>
              <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
                Find More
              </button>
            </div>

            <div className="space-y-4">
              {[
                { name: 'National Merit Scholarship', deadline: 'Oct 15, 2024', status: 'Applied', amount: '$2,500' },
                { name: 'Local Rotary Club Scholarship', deadline: 'Nov 30, 2024', status: 'In Progress', amount: '$1,000' },
                { name: 'Engineering Society Award', deadline: 'Dec 15, 2024', status: 'To Apply', amount: '$5,000' }
              ].map((scholarship, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-slate-900 text-sm">{scholarship.name}</h4>
                    <span className="text-sm font-bold text-emerald-600">{scholarship.amount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">Due: {scholarship.deadline}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      scholarship.status === 'Applied' ? 'bg-emerald-100 text-emerald-700' :
                      scholarship.status === 'In Progress' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {scholarship.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Complete Timeline */}
      <div className="bg-white rounded-2xl border-2 border-slate-900 shadow-xl p-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-8">Complete Timeline</h2>
        <div className="space-y-4">
          {timeline.slice(0, 5).map((item) => (
            <div key={item.id} className="flex items-center gap-4 p-5 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
              <input type="checkbox" className="w-5 h-5 rounded text-teal-600" checked={item.completed} readOnly />
              <div className="flex-1">
                <p className="font-semibold text-slate-900">{item.title}</p>
                <p className="text-sm text-slate-600">
                  Due {new Date(item.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
              </div>
              <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                item.priority === 'critical' ? 'bg-red-100 text-red-700' :
                item.priority === 'high' ? 'bg-amber-100 text-amber-700' :
                'bg-slate-100 text-slate-600'
              }`}>
                {item.priority}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CollegeJourneyHubEnhanced;